import React, { forwardRef, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  Site,
  SiteFilterInputObject,
  SitesSearchListQueryVariables,
  useSitesSearchListQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import useChipListField from '@lib/hooks/useChipListField';
import styles from './SiteChipListField.module.scss';

type Props = {
  fieldName?: string;
  ids?: string[];
  sectorId?: string;
  values: Values;
};

const SiteChipListField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const { fieldName = '', ids, sectorId = '', values } = props;
    const { t } = useTranslation();
    const [paginationParams, setPaginationParams] =
      useState<PaginationParams>(PAGINATION_PARAMS);

    const page = _get(paginationParams, GetUrlParams.Page);
    const perPage = _get(paginationParams, GetUrlParams.PerPage);
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );

    const queryVariables: SitesSearchListQueryVariables = {
      page: Number(page),
      limit: Number(perPage),
    };

    const filters: SiteFilterInputObject[] = [];
    if (sectorId) {
      filters.push({
        sectorId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: sectorId },
        },
      });
    }
    if (filters.length) {
      queryVariables.filters = filters;
    }
    if (orderingField && orderingDirection) {
      queryVariables.sorts = {
        [orderingField]: orderingDirection,
      };
    }

    const query = useSitesSearchListQuery({
      fetchPolicy: 'no-cache',
      variables: queryVariables,
      skip: !sectorId,
    });

    const newItems = _get(
      query.data || query.previousData,
      'data.collection',
      [],
    ) as Site[];

    const {
      visibleItems,
      newFormFieldValue,
      handleRemove,
      handleLoadMore,
      currentPage,
      totalPages,
      firstLoading,
      error,
    } = useChipListField<Site>({
      fieldName,
      ids,
      values,
      paginationParams,
      setPaginationParams,
      queryResult: query,
      newItems,
    });

    if (error || firstLoading) return null;

    const newFormFieldValueRender = newFormFieldValue?.length
      ? newFormFieldValue
          .filter(
            (formFieldItem) =>
              _get(formFieldItem, 'data.id') &&
              !visibleItems.some(
                (visibleItem) =>
                  _get(formFieldItem, 'data.id') === visibleItem.id,
              ),
          )
          .map((formFieldItem: SelectOption) => (
            <div key={formFieldItem.id}>
              <Chip
                type={ChipTypes.chipGray}
                size={ChipSizes.medium}
                label={formFieldItem.label}
                rightIconComponent={
                  <MaterialIcon
                    icon="cancel"
                    onClick={() => handleRemove(formFieldItem.value as string)}
                  />
                }
              />
            </div>
          ))
      : null;

    const visibleItemsRender = visibleItems?.length
      ? visibleItems.map((visibleItem) => (
          <div key={visibleItem.id}>
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.medium}
              label={visibleItem.name}
              rightIconComponent={
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(visibleItem.id)}
                />
              }
            />
          </div>
        ))
      : null;

    if (newFormFieldValueRender === null && visibleItemsRender === null)
      return null;

    return (
      <div>
        <div className={styles.chipsList}>
          {newFormFieldValueRender}
          {visibleItemsRender}
        </div>
        {currentPage < totalPages && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={handleLoadMore}
            data-test-id="site-chip-list-field__show-more"
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    );
  },
);

export default memo(SiteChipListField);
